import { AnalyticsEvents, BannerType, useApp, useTranslation } from "@next-platform/client-common-core";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import { Image } from "@next-platform/client-common-spicy-ui";
import React, { MouseEvent, useCallback, useMemo } from "react";

import { CmsBanner, LocalizedLink, SiteLogo } from "@/components";
import { FooterContent } from "@/components/footer/types";
import { DiscordThemeIcon, RedditThemeIcon, TwitterThemeIcon } from "@/components/icons";
import {
  AndroidDark,
  AndroidLight,
  AppStoreDark,
  AppStoreLight,
  EXTERNAL_ROUTES_FOR_APPLICATION,
  GetItOnPlayStoreDark,
  GetItOnPlayStoreLight,
  ORG_ADDRESSES,
  ROUTES,
  SOCIAL_MEDIA_EVENT_VALUE,
  SPICYCHAT_DISCORD,
  SPICYCHAT_REDDIT,
  SPICYCHAT_TWITTER,
} from "@/constants";
import useDownloadMobileApplicationsConfig from "@/features/mobileApplications/useDownloadMobileApplicationsConfig";
import { useBrand, useTheme } from "@/hooks";
import useLocalizedLocation from "@/hooks/useLocalizedLocation";
import useQueueLogEvent from "@/hooks/useQueueLogEvent";
import useAuth from "@/services/useAuth";
import { cn, getBusinessRouteScreen } from "@/utils";

const ICON_SIZE = 16;

const handleWebOnPress = (e: MouseEvent<HTMLAnchorElement>) => {
  e?.stopPropagation();
};

const getPlayStoreBadgeTheme = (isPixelChat: boolean) => {
  return isPixelChat ? [GetItOnPlayStoreDark, GetItOnPlayStoreLight] : [AndroidDark, AndroidLight];
};

const getApplicationBadges = ({
  appStoreBadge,
  hasAndroidApplication,
  hasIosApplication,
  playStoreBadge,
}: {
  appStoreBadge: string;
  hasAndroidApplication: boolean;
  hasIosApplication: boolean;
  playStoreBadge: string;
}) => {
  const badges = [];

  if (hasAndroidApplication) {
    badges.push({ src: playStoreBadge, to: ROUTES.download });
  }

  if (hasIosApplication) {
    badges.push({ src: appStoreBadge, to: ROUTES.download });
  }

  return badges;
};

function Socials({ desktop, footerContent }: { desktop: boolean; footerContent: FooterContent }) {
  if (!footerContent.socials || footerContent.socials.hidden) {
    return null;
  }

  return (
    <div className={cn("flex flex-col gap-1.5 flex-grow mt-1 desktop:mt-0", desktop ? "hidden desktop:flex" : "desktop:hidden")}>
      <span className="text-sm font-medium text-[#0F0F0F] dark:text-[#F2F2F2] hidden desktop:inline">{footerContent.socials.title}</span>

      <div className="flex flex-row gap-2.5 desktop:flex-col desktop:gap-1.5">
        {footerContent.socials.links.map((link, index) => {
          const { label, icon, ...rest } = link;

          return (
            <LocalizedLink key={index} {...rest}>
              <div className="flex flex-row gap-1.5 items-center">
                {icon}

                <span className="text-xs text-[#646464] dark:text-[#AAAAAA] hidden desktop:inline">{label}</span>
              </div>
            </LocalizedLink>
          );
        })}
      </div>
    </div>
  );
}

function Address({ mobile, footerContent }: { mobile: boolean; footerContent: FooterContent }) {
  if (!footerContent.address) {
    return null;
  }

  return (
    <div className={cn("flex-col gap-0.5", mobile ? "flex mob:hidden" : "hidden mob:flex")}>
      <span className="text-[10px] font-medium text-[#0F0F0F] dark:text-[#F2F2F2]">{footerContent.address.title}</span>

      {footerContent.address.addresses.map((address, index) => (
        <span className="text-[10px] text-[#B5B5B6] dark:text-[#737476]" key={index}>
          {address}
        </span>
      ))}
    </div>
  );
}

export default function Footer() {
  const { isSpicyChat, isPixelChat, isSecretMate } = useBrand();
  const { pagePath } = useLocalizedLocation();
  const { isDark } = useTheme();
  const screenName = getBusinessRouteScreen(pagePath);
  const { logEvent } = useQueueLogEvent();
  const { isAuthenticated } = useAuth();
  const { branding } = useApp();
  const applicationId = basicAppConfig.applicationId;
  const { customerSupportUrl, refundPolicyUrl, privacyPolicyUrl, tcUrl } = branding || {};
  const { t } = useTranslation();
  const handleSocialMediaClick = useCallback(
    (socialPlatform: string) => {
      logEvent(AnalyticsEvents.SOCIAL_MEDIA_CLICK, {
        social_media: socialPlatform,
        page_name: screenName,
      });
    },
    [logEvent, screenName],
  );

  const shouldShowComplianceLink = isSpicyChat || isSecretMate;
  const complianceLabel = shouldShowComplianceLink
    ? t("legal:s2257.complianceLabel", {
        defaultValue: "18 U.S.C. 2257 Record-Keeping Requirements Compliance Statement",
      })
    : "";
  const [playStoreBadgeDark, playStoreBadgeLight] = getPlayStoreBadgeTheme(isPixelChat);
  const appStoreBadge = isDark ? AppStoreDark : AppStoreLight;
  const { androidApplication, iosApplication } = useDownloadMobileApplicationsConfig();
  const applicationBadges = useMemo(
    () =>
      getApplicationBadges({
        appStoreBadge,
        hasAndroidApplication: Boolean(androidApplication),
        hasIosApplication: Boolean(iosApplication),
        playStoreBadge: isDark ? playStoreBadgeDark : playStoreBadgeLight,
      }),
    [androidApplication, appStoreBadge, iosApplication, isDark, playStoreBadgeDark, playStoreBadgeLight],
  );

  const footerContent: FooterContent = useMemo(
    () => ({
      logo: <SiteLogo />,
      address: {
        title: t("appFooter:legal.label"),
        addresses: ORG_ADDRESSES,
      },
      apps: applicationBadges,
      resources: {
        title: t("appFooter:resources.title"),
        links: [
          {
            label: t("appFooter:resources.complaints"),
            to: ROUTES.complaint || "",
          },
          {
            label: t("appFooter:resources.terms"),
            to: tcUrl || "",
          },
          {
            label: t("appFooter:resources.privacy"),
            to: privacyPolicyUrl || "",
          },
          {
            label: t("appFooter:resources.refund"),
            to: refundPolicyUrl || "",
          },
          {
            label: t("appFooter:resources.support"),
            to: customerSupportUrl || "",
            target: "_blank",
          },
          {
            label: t("appFooter:resources.blog"),
            to: (applicationId && EXTERNAL_ROUTES_FOR_APPLICATION[applicationId]?.blog) || "",
            target: "_blank",
            hidden: !isSpicyChat,
          },
          {
            label: t("appFooter:resources.login"),
            to: ROUTES.login,
            hidden: isAuthenticated,
          },
        ],
      },
      community: {
        title: t("appFooter:community.title"),
        links: [
          {
            label: t("appFooter:community.guidelines"),
            to: (applicationId && EXTERNAL_ROUTES_FOR_APPLICATION[applicationId]?.guidelines) || "",
            target: "_blank",
          },
          {
            label: t("appFooter:community.affiliate"),
            to: (applicationId && EXTERNAL_ROUTES_FOR_APPLICATION[applicationId]?.affiliates) || "",
            hidden: !isSpicyChat,
          },
          {
            label: t("appFooter:community.report"),
            to: ROUTES.report,
          },
        ],
      },
      socials: {
        title: t("appFooter:joinUs.title"),
        hidden: !isSpicyChat,
        links: [
          {
            icon: <DiscordThemeIcon size={ICON_SIZE} />,
            label: "Discord",
            to: SPICYCHAT_DISCORD,
            target: "_blank",
            onClick: () => handleSocialMediaClick(SOCIAL_MEDIA_EVENT_VALUE.Discord),
          },
          {
            icon: <TwitterThemeIcon size={ICON_SIZE} />,
            label: "Twitter",
            to: SPICYCHAT_TWITTER,
            target: "_blank",
            onClick: () => handleSocialMediaClick(SOCIAL_MEDIA_EVENT_VALUE.X),
          },
          {
            icon: <RedditThemeIcon size={ICON_SIZE} />,
            label: "Reddit",
            to: SPICYCHAT_REDDIT,
            target: "_blank",
            onClick: () => handleSocialMediaClick(SOCIAL_MEDIA_EVENT_VALUE.Reddit),
          },
        ],
      },
      compliance: {
        label: complianceLabel,
        hidden: !shouldShowComplianceLink,
        to: ROUTES.compliance,
      },
    }),
    [
      applicationId,
      applicationBadges,
      customerSupportUrl,
      handleSocialMediaClick,
      isSpicyChat,
      shouldShowComplianceLink,
      complianceLabel,
      privacyPolicyUrl,
      refundPolicyUrl,
      t,
      tcUrl,
      isAuthenticated,
    ],
  );

  function InternalLinkWrapper({
    id = "",
    to,
    children,
    target = "_self",
  }: {
    id?: string;
    to?: string;
    children: React.ReactNode;
    target?: string;
  }) {
    return (
      <LocalizedLink id={id} to={to ?? "#"} target={target} onClick={handleWebOnPress} aria-label={to}>
        {children}
      </LocalizedLink>
    );
  }

  return (
    <>
      <CmsBanner placement={BannerType.Bottom} className={"px-4 mt-1 max-mob:px-2.5"} isVisibleOnPartner={false} />

      <div className="flex flex-col py-6 gap-1.5 px-2 mob:px-3 desktop:px-4">
        <div className="flex flex-col mob:flex-row gap-5 flex-grow">
          <div className="flex flex-col gap-5 flex-grow flex-shrink">
            {footerContent.logo}
            <Address footerContent={footerContent} mobile={false} />
          </div>

          <div className="flex flex-row gap-2.5 flex-grow max-w-[557px]">
            {footerContent.resources || footerContent.socials ? (
              <div className="flex flex-col gap-0.5 flex-grow">
                <span className="text-sm font-medium text-[#0F0F0F] dark:text-[#F2F2F2]">{footerContent.resources.title}</span>

                {footerContent.resources.links.map((link, index) => {
                  const { label, hidden, ...rest } = link;
                  if (!hidden) {
                    return (
                      <LocalizedLink key={index} {...rest}>
                        <span className="text-xs text-[#646464] dark:text-[#AAAAAA]">{label}</span>
                      </LocalizedLink>
                    );
                  }
                })}

                <div className="mt-2.5" />

                <Socials footerContent={footerContent} desktop={false} />
              </div>
            ) : null}

            {footerContent.community ? (
              <div className="flex flex-col gap-0.5 flex-grow">
                <span className="text-sm font-medium text-[#0F0F0F] dark:text-[#F2F2F2]">{footerContent.community.title}</span>

                {footerContent.community.links.map((link, index) => {
                  const { label, hidden, ...rest } = link;
                  if (!hidden) {
                    return (
                      <LocalizedLink key={index} {...rest}>
                        <span className="text-xs text-[#646464] dark:text-[#AAAAAA]">{label}</span>
                      </LocalizedLink>
                    );
                  }
                })}
              </div>
            ) : null}
            <Socials footerContent={footerContent} desktop />
          </div>
        </div>

        {footerContent.apps?.length ? (
          <div className="flex flex-row gap-2.5 py-[18px]">
            {footerContent.apps.map((app, index) => {
              const { src, ...rest } = app;

              return (
                <InternalLinkWrapper key={index} {...rest}>
                  <Image className={cn("ring-1 rounded-md ring-[#CCCCCC] dark:ring-[#474747]")} src={src} width={95} height={30} />
                </InternalLinkWrapper>
              );
            })}
          </div>
        ) : null}

        <div className="h-[1px] bg-[#CCCCCC] dark:bg-[#474747] w-full" />

        <div className={"flex flex-col items-start mob:flex-row gap-2.5 justify-between mob:items-center"}>
          <Address footerContent={footerContent} mobile />

          {!footerContent?.compliance.hidden && (
            <LocalizedLink to={footerContent.compliance?.to}>
              <span className="text-[#0072F5] text-[10px]">{footerContent.compliance.label}</span>
            </LocalizedLink>
          )}
        </div>
      </div>
    </>
  );
}
